<template>
  <st-page :title="titleComputed">
    <Loader v-if="isLoading"/>

    <template #toolbar>
      <st-button :callback="onDownload">{{ $t("DOCUMENT_TEMPLATE.FORM.DOWNLOAD_BUTTON") }}</st-button>
      <st-button variant="secondary" :callback="openSaveModal">{{
        $t("GENERAL.BUTTON.SAVE")
      }}</st-button>
    </template>

    <div class="control-pane" ref="controlPane">
      <b-row class="h-100">
        <div class="col-md-2 flat-list-container">
          <div class="flat-list-title">{{ $t("DOCUMENT_TEMPLATE.FORM.FIELDS_LIST_TITLE") }}</div>
          <div class="form-group mx-4">
            <label>{{ $t("DOCUMENT_TEMPLATE.FORM.APPLICATION_TYPES_LABEL") }}</label>
            <b-form-select
              v-model="mainApplicationTypeId"
              name="main_application_type"
              :options="applicationTypesOptions"
              valueField="id"
              textField="name"
              @change="onMainApplicationTypeChanged"
            ></b-form-select>
          </div>

          <div v-if="hasRelatedApplicationTypes" class="form-group mx-4">
            <label>{{ $t("DOCUMENT_TEMPLATE.FORM.RELATED_APPLICATION_TYPES_LABEL") }}</label>
            <b-form-select
              v-model="relatedApplicationTypeId"
              name="related_application_type"
              :options="relatedApplicationTypesOptions"
              valueField="id"
              textField="name"
              @change="onRelatedApplicationTypeChanged"
            ></b-form-select>
          </div>

          <div class="form-group mx-4">
            <label>{{ $t("DOCUMENT_TEMPLATE.FORM.FORM_TYPE_LABEL") }}</label>
            <b-form-group >
              <b-form-radio v-model="selectedFormType" :value="CITIZEN_FORM_TYPE">{{ $t("DOCUMENT_TEMPLATE.FORM.CITIZEN_LABEL") }}</b-form-radio>
              <b-form-radio v-model="selectedFormType" :value="STAFF_FORM_TYPE">{{ $t("DOCUMENT_TEMPLATE.FORM.STAFF_LABEL") }}</b-form-radio>
            </b-form-group>
          </div>
          <ejs-listview
            id="flat-list"
            :dataSource="metaFields"
            :fields="fields"
            :select="onSelectField"
            showHeader='true'
            :height="listHeight"
          >
          </ejs-listview>
        </div>
        <div class="col-md-10 document-editor-container">
          <ejs-documenteditorcontainer
            id="container"
            ref="doceditcontainer"
            :enableToolbar="true"
            :height="pageHeight"
            :serviceUrl="syncfusionApiUrl"
          >
          </ejs-documenteditorcontainer>
        </div>
      </b-row>
    </div>
    <document-template-create-modal
      v-if="!record"
      ref='document-template-create-modal'
      @submit="submit"
      :loading="isLoadingCreate"
    />

    <document-template-edit-modal
      v-if="record"
      ref='document-template-edit-modal'
      @submit="submit"
      :loading="isLoadingEdit"
      :selectedItem="record"
    />
  </st-page>
</template>
<script>
import Vue from "vue";
import {
  DocumentEditorContainerPlugin,
  Toolbar,
} from "@syncfusion/ej2-vue-documenteditor";
import { ListViewPlugin } from "@syncfusion/ej2-vue-lists";
import { mapActions, mapGetters } from "vuex";

Vue.use(DocumentEditorContainerPlugin);
Vue.use(ListViewPlugin);

const START_TAG = "{";
const END_TAG = "}";

export default {
  name: 'DocumentTemplateForm',
  props: {
    id: String,
  },
  components: {
    DocumentTemplateCreateModal: () => import('@/modules/document-template/components/DocumentTemplateCreateModal.vue'),
    DocumentTemplateEditModal: () => import('@/modules/document-template/components/DocumentTemplateEditModal.vue'),
  },
  data() {
    const CITIZEN_FORM_TYPE = 'CITIZEN';
    const STAFF_FORM_TYPE = 'STAFF';
    return {
      metaFields: [],
      metaFieldsChildren: [],
      systemMetaFields: [],
      fields: { groupBy: "category", tooltip: "text" },
      toolbarItems: [
        "New",
        "Open",
        "Separator",
        "Undo",
        "Redo",
        "Separator",
        "Table",
        "Header",
        "Footer",
        "Break",
        "Separator",
        "FormFields",
        "PageNumber",
        "PageSetup"
      ],
      applicationTypesOptions: [],
      relatedApplicationTypesOptions: [],
      mainApplicationTypeId: null,
      mainApplicationType: null,
      relatedApplicationTypeId: null,
      selectedApplicationType: null,
      formFields: {},
      pageHeight: 0,
      listHeight: 0,
      CITIZEN_FORM_TYPE,
      STAFF_FORM_TYPE,
      selectedFormType: CITIZEN_FORM_TYPE,
    };
  },
  provide() {
    return {
      DocumentEditorContainer: [Toolbar],
    };
  },
  computed: {
    ...mapGetters({
      record: 'documentTemplate/form/record',
      loading: 'shared/loading',
      file: 'documentTemplate/form/file',
    }),
    isLoadingCreate() {
      return !!this.loading['documentTemplate/create']
        || !!this.loading['documentTemplate/fileUpload'];
    },
    isLoadingEdit() {
      return !!this.loading['documentTemplate/update']
        || !!this.loading['documentTemplate/fileUpload'];
    },
    isLoadingFind() {
      return !!this.loading['documentTemplate/find'];
    },
    isLoadingForm() {
      return !!this.loading['documentTemplate/findForm'];
    },
    isLoadingCollectMetadata() {
      return !!this.loading['documentTemplate/collectMetadata'];
    },
    isLoadingSystemMetadata() {
      return !!this.loading['documentTemplate/fetchSystemMetadata']
    },
    isLoading() {
      return this.isLoadingFind
        || this.isLoadingForm
        || this.isLoadingCollectMetadata
        || this.isLoadingSystemMetadata;
    },
    titleComputed() {
      if (!this.id) return this.$t('DOCUMENT_TEMPLATE.FORM.TITLE');
      return this?.record?.name;
    },
    hasRelatedApplicationTypes() {
      return this.selectedApplicationType && this.relatedApplicationTypesOptions?.length;
    },
    syncfusionApiUrl() {
      return window.VUE_APP_SYNCFUSION_API_URL;
    }
  },
  methods: {
    ...mapActions({
      doFetchApplicationTypes: "documentTemplate/form/fetchApplicationTypes",
      doFetchForms: "documentTemplate/form/fetchForms",
      doFindForm: "documentTemplate/form/findForm",
      doFileUpload: "documentTemplate/form/fileUpload",
      doCreate: "documentTemplate/form/create",
      doUpdate: "documentTemplate/form/update",
      doFind: 'documentTemplate/form/find',
      doClear: 'documentTemplate/form/clear',
      doCollectMetadata: 'documentTemplate/form/collectMetadata',
      doFetchSystemMetadata: 'documentTemplate/form/fetchSystemMetadata',
    }),
    async onMainApplicationTypeChanged(mainApplicationTypeId) {
      if (mainApplicationTypeId) {
        const mainApplicationType = this.applicationTypesOptions
          .find(el => el.id === mainApplicationTypeId);
        this.relatedApplicationTypesOptions = this.applicationTypesOptions
          .filter(el => mainApplicationType.related_application_type_ids.includes(el.id));
        this.mainApplicationType = mainApplicationType;
        this.selectedApplicationType = mainApplicationType;
      } else {
        this.selectedApplicationType = null;
        this.relatedApplicationTypeId = null;
        this.relatedApplicationTypesOptions = [];
      }
    },
    onRelatedApplicationTypeChanged(relatedApplicationTypeId) {
      if (relatedApplicationTypeId) {
        const relatedApplicationType = this.relatedApplicationTypesOptions
          .find(el => el.id === relatedApplicationTypeId);
        this.selectedApplicationType = relatedApplicationType;
      } else {
        this.selectedApplicationType = this.mainApplicationType;
      }

    },
    onDownload() {
      const format = 'Docx';
      this.$refs.doceditcontainer.ej2Instances.documentEditor.save(
        !this.id
          ? "SampleDocumentTemplate"
          : this.record.name,
        format
      );
    },
    insertPlaceholder(fieldMeta) {
      const fieldName = fieldMeta
        .replace(/\n/g, "")
        .replace(/\r/g, "")
        .replace(/\r\n/g, "");
      const fieldCode = "MERGEFIELD  " + fieldName + "  \\* MERGEFORMAT ";
      this.$refs.doceditcontainer.ej2Instances.documentEditor.editor.insertField(
        fieldCode,
        fieldName
      );
    },
    insertField(fieldMeta, fieldType) {
      const prefix = this.selectedApplicationType.key;
      const fieldName = fieldMeta
        .replace(/\n/g, "")
        .replace(/\r/g, "")
        .replace(/\r\n/g, "");

      let insertedField = '';
      if (fieldType === 'system') {
        insertedField = `${START_TAG}${prefix}.${fieldName}${END_TAG}`;
      } else {
        insertedField = `${START_TAG}${prefix}.${this.selectedFormType}.${fieldName}${END_TAG}`;
      }

      const fieldCode = "MERGEFIELD  " + fieldName + "  \\* MERGEFORMAT ";
      this.$refs.doceditcontainer.ej2Instances.documentEditor.editor.insertField(
        fieldCode,
        insertedField
      );
      this.$refs.doceditcontainer.ej2Instances.documentEditor.focusIn();
    },
    insertList(fieldMeta) {
      const prefix = this.selectedApplicationType.key;
      let fieldName = fieldMeta
        .replace(/\n/g, "")
        .replace(/\r/g, "")
        .replace(/\r\n/g, "");

      const insertedList = `${START_TAG}FOR item IN ${prefix}.${this.selectedFormType}.${fieldName}${END_TAG}\n${START_TAG}END-FOR item${END_TAG}`;
      const fieldCode = "MERGEFIELD  " + fieldName + "  \\* MERGEFORMAT ";
      this.$refs.doceditcontainer.ej2Instances.documentEditor.editor.insertField(
        fieldCode,
        insertedList
      );
      this.$refs.doceditcontainer.ej2Instances.documentEditor.focusIn();
    },
    insertChild(fieldMeta) {
      const fieldName = fieldMeta
        .replace(/\n/g, "")
        .replace(/\r/g, "")
        .replace(/\r\n/g, "");

      const insertedField = `${START_TAG}INS $item.${fieldName}${END_TAG}`;
      const fieldCode = "MERGEFIELD  " + fieldName + "  \\* MERGEFORMAT ";
      this.$refs.doceditcontainer.ej2Instances.documentEditor.editor.insertField(
        fieldCode,
        insertedField
      );
      this.$refs.doceditcontainer.ej2Instances.documentEditor.focusIn();
    },
    insertCheckbox(fieldMeta) {
      const prefix = this.selectedApplicationType.key;
      let fieldName = fieldMeta
        .replace(/\n/g, "")
        .replace(/\r/g, "")
        .replace(/\r\n/g, "");

      let parentField = `options.${fieldName}`;

      const insertedList = `${START_TAG}FOR item IN ${prefix}.${this.selectedFormType}.${parentField}${END_TAG}
      ${START_TAG}=${prefix}.${this.selectedFormType}.${fieldName}.includes($item) ? '[X] ' : '[ ] '${END_TAG}${START_TAG} INS $item ${END_TAG}
      ${START_TAG}END-FOR item${END_TAG}`;
      const fieldCode = "MERGEFIELD  " + fieldName + "  \\* MERGEFORMAT ";

      const formattedList = insertedList.replace(/\s\s+/g, " ");
      this.$refs.doceditcontainer.ej2Instances.documentEditor.editor.insertField(
        fieldCode,
        formattedList
      );
      this.$refs.doceditcontainer.ej2Instances.documentEditor.focusIn();
    },
    insertMetadata(text, type) {
      switch (type) {
        case 'beneficiary-list':
        case 'real-estate-list':
        case 'decision-list':
          this.insertList(text);
          break;
        case 'child':
          this.insertChild(text);
          break;
        case 'checkbox':
          this.insertCheckbox(text);
          break;
        case 'sign':
          this.insertPlaceholder(text);
          break;
        default:
          this.insertField(text, type);
          break;
      }
    },
    onSelectField(args) {
      let fieldName = args.text;
      const fieldType = args.data.type;

      if (fieldType === 'sign') {
        fieldName = args.data.key;
      }

      this.insertMetadata(fieldName, fieldType);
    },
    openSaveModal() {
      if (this.id) {
        this.$refs['document-template-edit-modal'].show();
      } else {
        this.$refs['document-template-create-modal'].show();
      }
    },
    async submit(item) {
      const doc = await this.$refs.doceditcontainer.ej2Instances.documentEditor.saveAsBlob("Docx");
      const sfdt = await this.$refs.doceditcontainer.ej2Instances.documentEditor.saveAsBlob("Sfdt");
      const jsonFile = new Blob([await sfdt.text()], { type: "plain/text" });

      let response;

      if (this.id) {
        response = await this.doUpdate({
          id: this.id,
          values: {
            ...item,
          }
        });
      } else {
        response = await this.doCreate({
          ...item,
          files: {
            sfdt: "",
            docx: "",
          },
        });
      }

      const { data, uploadAccessToken } = response;
      const documentTemplate = data;
      const formData = new FormData();
      formData.append("files", doc, 'Cerere urbanism docx');
      formData.append("files", jsonFile, 'Cerere urbanism sfdt');

      this.doFileUpload({
        formData,
        uploadToken: uploadAccessToken,
      })
      .then((data) => {
        this.doUpdate({
          id: documentTemplate.id,
          values: {
            files: {
              docx: data[0].id,
              sfdt: data[1].id,
            },
          },
        }).
        then(() => {
          this.$router.push({ name: 'documentTemplate' });
        });
      });
    },
    async updateMetaFields(applicationType) {
      if (applicationType) {
        let formId;
        let rejectedFormId = "";

        if (this.selectedFormType === this.CITIZEN_FORM_TYPE) {
          formId = applicationType.form_citizen_id;
        } else {
          formId = applicationType.form_staff_id;
          rejectedFormId = applicationType?.reject_form_staff_id ?? "";
        }

        const form = await this.doFindForm(formId);
        this.metaFields = form.fields_map.map(field => ({
          text: field.name,
          htmlAttributes: { draggable: true },
          category: this.$t(`GENERAL.${this.selectedFormType}`),
          type: field.type,
          id: this.generateRandomId(),
          child: field.child.map(field => ({
            text: field.name,
            htmlAttributes: { draggable: true },
            type: field.type,
            id: this.generateRandomId(),
          })),
        }));

        //system meta have to appear all the time
        this.metaFields = [ ...this.systemMetaFields, ...this.metaFields];

        if (rejectedFormId) {
          const form = await this.doFindForm(rejectedFormId);
          const rejectedMetaFields = form.fields_map.filter((field) => {
            const isDuplicate = this.metaFields.find((el) => el.text === field.name);
            if (!isDuplicate) {
              return field;
            }
          }).map((field) => ({
            text: field.name,
            htmlAttributes: { draggable: true },
            category: this.$t(`GENERAL.${this.selectedFormType}`),
            type: field.type,
            id: this.generateRandomId(),
            child: field.child.map(field => ({
              text: field.name,
              htmlAttributes: { draggable: true },
              type: field.type,
              id: this.generateRandomId(),
            })),
          }));

          this.metaFields = [...rejectedMetaFields, ...this.metaFields];
        }

        if (this.selectedFormType === this.STAFF_FORM_TYPE) {
          let metaWorkflow = await this.doCollectMetadata(applicationType.workflow_id);
          metaWorkflow = metaWorkflow.map((meta) => ({
            text: meta.name,
            htmlAttributes: { draggable: true },
            category: meta?.type === 'sign' ? this.$t('GENERAL.SIGN') : this.$t('GENERAL.SYSTEM'),
            type: meta.type,
            key: meta.sign_placeholder,
            id: this.generateRandomId()
          }));

          this.metaFields = [ ...this.metaFields, ...metaWorkflow ];
        }
      }

      //grids/sections/dividers should not be included in metafields
      this.metaFields = this.metaFields?.filter((el) => el.text !== undefined);

      //separate child properties to fix drag/drop error
      let relatedMeta = this.metaFields.map((el) => el?.child ?? []).filter((el) => el?.length);
      for (let i=0; i < relatedMeta.length; i++) {
        this.metaFieldsChildren = [...this.metaFieldsChildren, ...relatedMeta[i]];
      }
    },
    generateRandomId() {
      return `id${Math.random().toString(36).substr(2, 10)}`;
    }
  },
  watch: {
    selectedApplicationType: {
      handler: async function(value) {
        await this.updateMetaFields(value);
      }
    },
    selectedFormType: {
      handler: async function(value) {
        if (value) {
          await this.updateMetaFields(this.selectedApplicationType);
        }
      }
    }
  },
  async created() {
    this.applicationTypesOptions = await this.doFetchApplicationTypes();

    const systemMetadataOptions = await this.doFetchSystemMetadata();
    if (systemMetadataOptions.length) {
       this.systemMetaFields = systemMetadataOptions.map((meta) => ({
          text: meta.name,
          htmlAttributes: { draggable: true },
          category: meta?.category === 'sign' ? this.$t('GENERAL.SIGN') : this.$t('GENERAL.SYSTEM'),
          id: this.generateRandomId(),
          type: meta?.type === 'sign' ? 'sign' : 'system',
          key: meta?.key
        }));
    }

    if (this.id) {
      await this.doFind(this.id);
      const obj = this.$refs.doceditcontainer.ej2Instances.documentEditor;
      obj.open(JSON.stringify(this.file));
    } else {
      await this.doClear();
    }
  },
  mounted() {
    const h = this.$refs.controlPane.clientHeight;
    this.pageHeight = h - 35;
    this.listHeight = h - 150;

    this.$nextTick(function () {
      this.$refs.doceditcontainer.ej2Instances.documentEditor.focusIn();
    });

    const items = this.toolbarItems;
    this.$refs.doceditcontainer.ej2Instances.toolbarItems = items;
    document
      .getElementById("flat-list")
      .addEventListener("dragstart", (event) => {
        let dataUid = event.target.getAttribute('data-uid');
        let metaValue = this.metaFields.find((el) => el.id === dataUid);
        if (!metaValue) {
          metaValue = this.metaFieldsChildren.find((el) => el.id === dataUid);
        }

        event.dataTransfer.setData("data", JSON.stringify(metaValue));
        event.target.classList.add("de-drag-target");
      });

    // Prevent default drag over for document editor element
    document
      .getElementById("container")
      .addEventListener("dragover", (event) => {
        event.preventDefault();
      });

    // Drop Event for document editor element
    document.getElementById("container").addEventListener("drop", (e) => {
      const data = e.dataTransfer.getData("data");
      let { text, type, key } = JSON.parse(data);

      this.$refs.doceditcontainer.ej2Instances.documentEditor.selection.select({
        x: e.offsetX,
        y: e.offsetY,
        extend: false,
      });

      if (type === 'sign') {
        text = key;
      }
      this.insertMetadata(text, type);
    });

    document.addEventListener("dragend", (event) => {
      if (event.target.classList.contains("de-drag-target")) {
        event.target.classList.remove("de-drag-target");
      }
    });
  },
};
</script>

<style scoped lang="scss" src="@/assets/sass/pages/document-template/document-template.scss"></style>
